"use client";

import {
    useEffect,
    useRef,
} from "react";
import {
    usePathname,
} from "next/navigation";

const TIKTOK_PIXEL_ID =
    "DAC0T3JC77UBCVGL1O40";

const COOKIE_CONSENT_KEY =
    "cookieConsent";

const TIKTOK_PIXEL_SCRIPT_ID =
    "tiktok-pixel-script";

const IS_PRODUCTION =
    process.env.NODE_ENV ===
    "production";

function isTikTokPixelEnabled(
    consent
) {
    return (
        IS_PRODUCTION ||
        consent
            ?.developmentTracking ===
        true
    );
}

function shouldLog() {
    return (
        !IS_PRODUCTION &&
        window
            ?.tiktokPixelDevelopmentTracking ===
        true
    );
}

function tiktokLog(
    message,
    details
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    if (
        details !== undefined
    ) {
        console.log(
            message,
            details
        );

        return;
    }

    console.log(
        message
    );
}

function tiktokError(
    message,
    error
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    console.error(
        message,
        error
    );
}

function getSavedConsent() {
    try {
        const savedConsent =
            window.sessionStorage.getItem(
                COOKIE_CONSENT_KEY
            );

        if (!savedConsent) {
            return null;
        }

        return JSON.parse(
            savedConsent
        );
    } catch (error) {
        tiktokError(
            "[TIKTOK] A mentett hozzájárulás nem olvasható.",
            error
        );

        return null;
    }
}

function deleteCookie(
    name
) {
    const hostname =
        window.location.hostname;

    const domains = [
        "",
        hostname,
        `.${hostname}`,
        ".strike.hu",
    ];

    domains.forEach(
        (domain) => {
            const domainPart =
                domain
                    ? `; domain=${domain}`
                    : "";

            document.cookie =
                `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
        }
    );
}

function removeTikTokCookies() {
    const cookieNames =
        document.cookie
            .split(";")
            .map(
                (cookie) =>
                    cookie
                        .split("=")[0]
                        .trim()
            )
            .filter(
                (cookieName) =>
                    cookieName ===
                    "_ttp" ||
                    cookieName ===
                    "ttclid" ||
                    cookieName ===
                    "ttcsid" ||
                    cookieName.startsWith(
                        "ttcsid_"
                    )
            );

    cookieNames.forEach(
        deleteCookie
    );
}

function createTikTokQueue() {
    if (
        typeof window ===
        "undefined"
    ) {
        return null;
    }

    window.TiktokAnalyticsObject =
        "ttq";

    const ttq =
        window.ttq =
            window.ttq ||
            [];

    if (
        ttq
            .setAndDefer
    ) {
        return ttq;
    }

    ttq.methods = [
        "page",
        "track",
        "identify",
        "instances",
        "debug",
        "on",
        "off",
        "once",
        "ready",
        "alias",
        "group",
        "enableCookie",
        "disableCookie",
        "holdConsent",
        "revokeConsent",
        "grantConsent",
    ];

    ttq.setAndDefer =
        function setAndDefer(
            target,
            method
        ) {
            target[method] =
                function deferredMethod() {
                    target.push(
                        [
                            method,
                        ].concat(
                            Array.prototype.slice.call(
                                arguments,
                                0
                            )
                        )
                    );
                };
        };

    for (
        let index = 0;
        index <
        ttq.methods.length;
        index += 1
    ) {
        ttq.setAndDefer(
            ttq,
            ttq.methods[
                index
            ]
        );
    }

    ttq.instance =
        function instance(
            pixelId
        ) {
            const instanceQueue =
                ttq._i?.[
                    pixelId
                ] ||
                [];

            for (
                let index = 0;
                index <
                ttq.methods.length;
                index += 1
            ) {
                ttq.setAndDefer(
                    instanceQueue,
                    ttq.methods[
                        index
                    ]
                );
            }

            return instanceQueue;
        };

    return ttq;
}

function sendTikTokPageView() {
    if (
        typeof window ===
            "undefined" ||
        !window.ttq ||
        !window
            .tiktokPixelInitialized ||
        !window
            .tiktokPixelTrackingEnabled
    ) {
        return;
    }

    window.ttq.page();

    tiktokLog(
        "[TIKTOK] PageView elküldve.",
        {
            pageLocation:
                window.location
                    .href,
            pageTitle:
                document.title,
        }
    );
}

function initializeTikTokPixel(
    consent
) {
    if (
        typeof window ===
        "undefined" ||
        !isTikTokPixelEnabled(
            consent
        )
    ) {
        return;
    }

    window
        .tiktokPixelDevelopmentTracking =
        consent
            ?.developmentTracking ===
        true;

    if (
        !TIKTOK_PIXEL_ID
    ) {
        return;
    }

    if (
        window
            .tiktokPixelInitialized
    ) {
        window
            .tiktokPixelTrackingEnabled =
            true;

        if (
            typeof window.ttq
                ?.grantConsent ===
            "function"
        ) {
            window.ttq
                .grantConsent();
        }

        return;
    }

    window
        .tiktokPixelInitialized =
        true;

    window
        .tiktokPixelTrackingEnabled =
        true;

    const ttq =
        createTikTokQueue();

    if (!ttq) {
        return;
    }

    ttq.grantConsent();

    ttq._i =
        ttq._i || {};

    ttq._i[
        TIKTOK_PIXEL_ID
    ] = [];

    ttq._i[
        TIKTOK_PIXEL_ID
    ]._u =
        "https://analytics.tiktok.com/i18n/pixel/events.js";

    ttq._t =
        ttq._t || {};

    ttq._t[
        TIKTOK_PIXEL_ID
    ] =
        +new Date();

    ttq._o =
        ttq._o || {};

    ttq._o[
        TIKTOK_PIXEL_ID
    ] = {};

    const existingScript =
        document.getElementById(
            TIKTOK_PIXEL_SCRIPT_ID
        );

    if (
        !existingScript
    ) {
        const script =
            document.createElement(
                "script"
            );

        script.id =
            TIKTOK_PIXEL_SCRIPT_ID;

        script.type =
            "text/javascript";

        script.async =
            true;

        script.src =
            `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${TIKTOK_PIXEL_ID}&lib=ttq`;

        script.addEventListener(
            "load",
            () => {
                tiktokLog(
                    "[TIKTOK] TikTok Pixel betöltve."
                );
            }
        );

        script.addEventListener(
            "error",
            (error) => {
                tiktokError(
                    "[TIKTOK] TikTok Pixel betöltése sikertelen.",
                    error
                );
            }
        );

        document.head.appendChild(
            script
        );
    }

    sendTikTokPageView();
}

function disableTikTokPixelTracking() {
    if (
        typeof window ===
        "undefined"
    ) {
        return;
    }

    window
        .tiktokPixelTrackingEnabled =
        false;

    window
        .tiktokPixelDevelopmentTracking =
        false;

    if (
        typeof window.ttq
            ?.revokeConsent ===
        "function"
    ) {
        window.ttq
            .revokeConsent();
    }

    removeTikTokCookies();

    tiktokLog(
        "[TIKTOK] Marketing hozzájárulás visszavonva."
    );
}

export function trackTikTokEvent(
    eventName,
    parameters = {}
) {
    if (
        typeof window ===
        "undefined" ||
        !window.ttq ||
        !window
            .tiktokPixelInitialized ||
        !window
            .tiktokPixelTrackingEnabled
    ) {
        return;
    }

    window.ttq.track(
        eventName,
        parameters
    );
}

export default function TikTokPixel() {
    const pathname =
        usePathname();

    const previousPathnameRef =
        useRef(null);

    const skipNextPageViewRef =
        useRef(false);

    useEffect(() => {
        const handleConsentSaved =
            (event) => {
                const nextConsent =
                    event.detail
                        ?.consent ||
                    null;

                if (
                    nextConsent
                        ?.marketing ===
                        true &&
                    isTikTokPixelEnabled(
                        nextConsent
                    )
                ) {
                    const wasInitialized =
                        window
                            .tiktokPixelInitialized ===
                        true;

                    initializeTikTokPixel(
                        nextConsent
                    );

                    if (
                        !wasInitialized
                    ) {
                        skipNextPageViewRef.current =
                            true;
                    }

                    return;
                }

                disableTikTokPixelTracking();
            };

        const savedConsent =
            getSavedConsent();

        if (
            savedConsent
                ?.marketing ===
                true &&
            isTikTokPixelEnabled(
                savedConsent
            )
        ) {
            initializeTikTokPixel(
                savedConsent
            );

            skipNextPageViewRef.current =
                true;
        } else {
            disableTikTokPixelTracking();
        }

        window.addEventListener(
            "cookie-consent-saved",
            handleConsentSaved
        );

        return () => {
            window.removeEventListener(
                "cookie-consent-saved",
                handleConsentSaved
            );
        };
    }, []);

    useEffect(() => {
        if (
            !pathname
        ) {
            return;
        }

        if (
            previousPathnameRef.current ===
            null
        ) {
            previousPathnameRef.current =
                pathname;

            return;
        }

        if (
            previousPathnameRef.current ===
            pathname
        ) {
            return;
        }

        previousPathnameRef.current =
            pathname;

        if (
            skipNextPageViewRef.current
        ) {
            skipNextPageViewRef.current =
                false;

            return;
        }

        sendTikTokPageView();
    }, [
        pathname,
    ]);

    return null;
}